// supervisor-attempt-cycle.ts
//
// One full attempt for the scheduler's next ready ticket (architecture
// §6.1): persist attempt start + baseline -> buildDispatchSpec (contract_error
// => no spawn) -> session-runner (P2, injectable spawn for E2E fakes) ->
// result-verifier (rehashes binding before any diff). Routing the verifier's
// verdict into retry/quota/commit lives in supervisor-outcome-router.ts
// (kept separate to stay under the 200-line budget and because routing has
// its own non-trivial branching).

import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { join } from "node:path";
import {
  type AttemptBaselineInventory,
  captureBaselineInventory,
} from "./attempt-baseline-inventory.js";
import { removeBindingAfterReap } from "./binding-rehash-and-cleanup.js";
import { computeAttemptInputHash } from "./canonical-json-and-checksum.js";
import { knownValuesFor } from "./mission-secrets-test-env-loader.js";
import { type VerifierVerdict, verifyAttemptResult } from "./result-verifier.js";
import type { RuntimeLimits } from "./runtime-limits-from-effective-config.js";
import { buildDispatchSpec, type SchedulerProjection } from "./scheduler.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import {
  runClaudeSession,
  type SessionRunnerDeps,
  type SessionRunResult,
} from "./session-runner.js";
import { buildVerifyAttemptInput } from "./supervisor-attempt-verify-input.js";
import { mergeAllowedTools } from "./verification-recipe-bash-permission-rules.js";

export interface AttemptCycleInput {
  projection: SchedulerProjection;
  ticket: Ticket;
  missionId: string;
  missionWorkspace: string;
  repoRoot: string;
  attemptNo: number;
  runtimeLimitsForKind: (kind: string) => RuntimeLimits;
  sessionDeps: SessionRunnerDeps;
  prompt: string;
  /** Dogfood F-2: Bash rules from the ticket's approved recipe (verification-recipe-bash-permission-rules.ts), appended to the adapter's grants. */
  recipeBashRules?: readonly string[];
  /** D03/N24: config's `worker_model` — always passed to argv as `--model`, never left to the worker binary's own default. Optional only for callers (tests) that don't care. */
  workerModel?: string;
  /**
   * D08: caller-supplied `run_id`, so `supervisor-loop.ts` can compute
   * `input_hash` (needs run_id) and journal it on `attempt_started` BEFORE
   * this cycle runs, while the session itself still uses the SAME run_id
   * end to end. Falls back to a fresh UUID for any other caller (tests)
   * that doesn't care about pre-journaling input_hash.
   */
  runId?: string;
  /** P1 R2: same pre-journal path as `runId` — the `sessionId` on attempt_started; fresh UUID otherwise. */
  sessionId?: string;
}

export type AttemptCycleResult =
  | { kind: "contract_error"; reason: string }
  | {
      kind: "verified";
      runId: string;
      sessionId: string;
      sessionResult: SessionRunResult;
      verdict: VerifierVerdict;
      baseline: AttemptBaselineInventory;
      /** D08: controller-computed `input_hash` for this attempt — surfaced so the caller (supervisor-loop.ts) can journal it on `attempt_started`. */
      inputHash: string;
    };

/**
 * Runs the dispatched ticket through session-runner then result-verifier.
 * `contract_error` before spawn (missing adapter) never touches the runner.
 */
export async function runAttemptCycle(input: AttemptCycleInput): Promise<AttemptCycleResult> {
  const dispatch = buildDispatchSpec(input.ticket);
  if (dispatch.kind === "contract_error") {
    return { kind: "contract_error", reason: dispatch.reason };
  }

  const runId = input.runId ?? randomUUID();
  const sessionId = input.sessionId ?? randomUUID();
  const stagingDir = join(input.repoRoot, ".avatar", "attempt-output", runId);
  await fs.mkdir(stagingDir, { recursive: true });

  // D08: input_hash is controller-computed, BEFORE dispatch — from data the
  // controller already holds (run_id/ticket_id/ticket_revision + a hash of
  // the exact prompt text the worker is about to receive). No worker
  // capability required (see autopilot-result-schema.ts's comment on why
  // this moved off the worker).
  const inputHash = computeAttemptInputHash({
    run_id: runId,
    ticket_id: input.ticket.id,
    ticket_revision: input.ticket.revision,
    prompt: input.prompt,
  });

  const baseline = await captureBaselineInventory(input.repoRoot);
  const limits = input.runtimeLimitsForKind(input.ticket.kind);

  const sessionResult = await runClaudeSession(
    {
      missionId: input.missionId,
      ticketId: input.ticket.id,
      runId,
      sessionId,
      kind: input.ticket.kind,
      missionWorkspace: input.missionWorkspace,
      packRoot: join(input.missionWorkspace, ".claude", "pack"),
      repoRoot: input.repoRoot,
      prompt: input.prompt,
      allowedTools: mergeAllowedTools(dispatch.allowedTools, input.recipeBashRules ?? []),
      allowedPaths: input.ticket.allowed_paths,
      workerModel: input.workerModel,
      // Phase 04a (B20): stream worker che known-values đọc LÚC SPAWN —
      // `test.env` tạo sau khi `run` mở vẫn được che. Chỉ để redact stream,
      // KHÔNG vào env/argv/prompt của worker (I3).
      secretValues: await knownValuesFor(input.missionId),
    },
    limits,
    input.sessionDeps,
  );

  if (sessionResult.outcome !== "ok" || sessionResult.bindingPath === undefined) {
    // No result to verify — outcome-router classifies this branch directly
    // from the session outcome (quota/network/contract_error/etc.) without
    // ever calling the verifier (nothing was produced to diff).
    // The verifier is also what normally removes the binding, so remove it
    // here — but ONLY once the worker's process group is reaped (a live
    // grandchild hook may still be reading it). Dogfood Bug E root cause: a
    // leftover binding from this branch was swept into the next run's
    // bootstrap commit. Best-effort: forensics never depend on this file.
    await removeBindingAfterReap(sessionResult.bindingPath, sessionResult.groupReaped);
    return {
      kind: "verified",
      runId,
      sessionId,
      sessionResult,
      verdict: { kind: "rejected", reasons: [sessionResult.reason ?? sessionResult.outcome] },
      baseline,
      inputHash,
    };
  }

  // NOTE (code-reviewer 260909-p6-m2 H1 follow-up): result-verifier.ts's
  // `relatedRecipeChecks` (step 10, unit-tested by test 25) exists for a
  // caller that has ALREADY determined which OTHER slice's recipe a
  // proposal's target artifact affects, and passes exactly that recipe.
  // Blanket-rerunning every approved recipe here for ANY non-empty
  // `proposed_changes` was tried and reverted: it doubles the attempt's
  // recipe-run cost inside the SAME `limits.deadlineMs` budget (verified
  // empirically against the two-slice-app fixture — the second full-suite
  // run starved the timeout and came back with an empty stdout tail,
  // producing a false `baseline_regression_detected` on a perfectly valid
  // attempt). proposal-applier.ts's own tickets-only mutation (this phase's
  // actual scope) never touches source artifacts, so no related-recipe
  // rerun is needed for it; a FUTURE proposal type that touches shared
  // source is P7+ scope and should pass a precise, targeted
  // `relatedRecipeChecks` list, not "rerun everything".
  //
  // Verify-input assembly (loadApprovedRecipes + field mapping) split to
  // supervisor-attempt-verify-input.ts (phase 04, D12 — trần dòng). H9: an
  // unreadable profile (missing/corrupt mid-run; `run`'s preflight only sees
  // the start) is this attempt's contract_error, never a throw that leaves
  // the ticket `in_progress`.
  let verifyInput: Awaited<ReturnType<typeof buildVerifyAttemptInput>>;
  try {
    verifyInput = await buildVerifyAttemptInput({
      input,
      sessionResult,
      limits,
      inputHash,
      runId,
      stagingDir,
      baseline,
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return {
      kind: "contract_error",
      reason: `verification_profile_unreadable: .avatar/verification-profile.json — ${detail.slice(0, 300)}`,
    };
  }
  const verdict = await verifyAttemptResult(verifyInput);

  return { kind: "verified", runId, sessionId, sessionResult, verdict, baseline, inputHash };
}
